import { getOpenAnalystAccountAccessTokenForRoute, getOpenAnalystApiAccessToken } from "@/context/auth/broker"
import { localDevAuthBypass } from "@/context/auth/dev-bypass"
import { openAnalystWebBaseUrl } from "@/utils/openanalyst-hosts"
import { queryOptions } from "@/utils/query-options"

export type OnboardingDefaultRoute = "home" | "smartwork_apps" | "smartwork_plugins" | "smartwork_integrations"

export type OnboardingRecommendationRoute = OnboardingDefaultRoute

export type OnboardingRecommendedApp = {
  id: string
  title: string
  description?: string | null
  route: OnboardingRecommendationRoute
}

export type OnboardingRecommendedPlugin = {
  hostedPluginId: string
  title: string
  description?: string | null
  ctaLabel?: string | null
}

export type OnboardingBootstrap = {
  pending: boolean
  onboardingId?: string | null
  title?: string | null
  subtitle?: string | null
  welcomeMessage?: string | null
  defaultRoute?: OnboardingDefaultRoute | null
  recommendedApps?: OnboardingRecommendedApp[] | null
  recommendedPlugins?: OnboardingRecommendedPlugin[] | null
}

type ErrorPayload = {
  error?: {
    message?: string
  }
  message?: string
}

const authBaseUrl = openAnalystWebBaseUrl()
let configuredFetch: typeof fetch | null = null

export function configureOnboardingFetch(fetcher: typeof fetch | null) {
  configuredFetch = fetcher
}

function authUrl(path: string) {
  return `${authBaseUrl}${path.startsWith("/") ? path : `/${path}`}`
}

function isSmartworkHostedAuthBaseUrl() {
  return /^https:\/\/(smartwork\.(arjun\.tv|openanalyst\.com)|cloud\.openanalyst\.com)$/.test(authBaseUrl)
}

function onboardingAccessToken() {
  return isSmartworkHostedAuthBaseUrl()
    ? getOpenAnalystAccountAccessTokenForRoute() ?? getOpenAnalystApiAccessToken()
    : getOpenAnalystApiAccessToken() ?? getOpenAnalystAccountAccessTokenForRoute()
}

async function parseJson(response: Response) {
  const text = await response.text()
  if (!text) return undefined
  try {
    return JSON.parse(text) as unknown
  } catch {
    return { message: text } as ErrorPayload
  }
}

async function onboardingRequest<T>(path: string, input?: { method?: string; body?: unknown }) {
  const headers = new Headers()
  if (input?.body !== undefined) headers.set("Content-Type", "application/json")
  const accessToken = onboardingAccessToken()
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`)
  const response = await (configuredFetch ?? fetch)(authUrl(path), {
    method: input?.method ?? "GET",
    credentials: "include",
    headers,
    body: input?.body === undefined ? undefined : JSON.stringify(input.body),
  })
  if (!response.ok) {
    const payload = (await parseJson(response)) as ErrorPayload | undefined
    throw new Error(payload?.error?.message || payload?.message || `Request failed with status ${response.status}`)
  }
  return (await parseJson(response)) as T
}

export function resolveOnboardingRoute(route?: OnboardingDefaultRoute | null) {
  if (route === "smartwork_apps") return "/smartwork?view=apps"
  if (route === "smartwork_plugins") return "/smartwork?view=plugins"
  if (route === "smartwork_integrations") return "/smartwork?view=integrations"
  return "/"
}

export function resolveOnboardingNavigation(input: {
  returnTo?: string | null
  defaultRoute?: OnboardingDefaultRoute | null
}) {
  return input.returnTo || resolveOnboardingRoute(input.defaultRoute)
}

export async function loadOnboardingBootstrap() {
  if (localDevAuthBypass()) {
    return {
      pending: false,
      onboardingId: null,
      title: null,
      subtitle: null,
      welcomeMessage: null,
      defaultRoute: null,
      recommendedApps: [],
      recommendedPlugins: [],
    } satisfies OnboardingBootstrap
  }

  const data = await onboardingRequest<OnboardingBootstrap>("/api/v1/onboarding/bootstrap")
  return {
    pending: data.pending === true,
    onboardingId: data.onboardingId ?? null,
    title: data.title ?? null,
    subtitle: data.subtitle ?? null,
    welcomeMessage: data.welcomeMessage ?? null,
    defaultRoute: data.defaultRoute ?? null,
    recommendedApps: data.recommendedApps ?? [],
    recommendedPlugins: data.recommendedPlugins ?? [],
  } satisfies OnboardingBootstrap
}

export async function acknowledgeOnboarding(onboardingId: string) {
  return onboardingRequest<{ success?: boolean }>("/api/v1/onboarding/ack", {
    method: "POST",
    body: { onboardingId },
  })
}

export const onboardingBootstrapQueryOptions = (userId?: string | null) =>
  queryOptions({
    queryKey: ["onboarding", "bootstrap", userId ?? "anonymous"] as const,
    queryFn: loadOnboardingBootstrap,
    staleTime: 60_000,
  })
